import {Card, Modal, Spinner} from 'react-bootstrap'
import {useEffect, useState} from 'react'
import {useLazyQuery} from '@apollo/client'
import {GET_ADVISORS_COMPLETED_POLICIES} from '../../../gql/queries/reportsQuery'
import './AdvisorsCompletedPoliciesTable.css'

interface IUser {
  id: string
  firstName: string
  lastName: string
  email: string
}

interface IAdvisorCompletedData {
  user: IUser
  completedCount: number
  carrierAccountCount: number
  renewedCount: number
  totalActiveCount: number
  totalNonCanceledCount: number
  totalCount: number
  canceledCount: number
  completionPercentage: number
  renewalPercentage: number
  canceledPercentage: number
  rank: number
  assignmentGroupName?: string
}

interface IResAdvisorsCompleted {
  advisorsCompletedPolicies: IAdvisorCompletedData[]
}

interface IAdvisorsCompletedPoliciesTableProps {
  dateFrom: string
  dateTo: string
  dateCriteria: 'submitted' | 'effective'
  carrier?: string
  stateEu?: string
  policyType?: string
  agent?: string[]
  paymentStatus?: string[]
  advisorPaid?: boolean
  loading?: boolean
  completedCategoryCodes?: string[]
  nonCanceledCategoryCodes?: string[]
  canceledCategoryCodes?: string[]
  onOpenPolicies?: (extraFilters: Record<string, unknown>) => void
  refreshTrigger?: number
}

type MetricColumn =
  | 'completed'
  | 'carrierAccount'
  | 'renewed'
  | 'totalActive'
  | 'totalNonCanceled'
  | 'canceled'
  | 'total'

type SortField = 'advisor' | 'completed' | 'completionPercentage' | 'carrierAccount' | 'renewed' | 'renewalPercentage' | 'totalActive' | 'totalNonCanceled' | 'total' | 'canceled' | 'canceledPercentage'
type SortOrder = 'asc' | 'desc'

export const AdvisorsCompletedPoliciesTable = ({
  dateFrom,
  dateTo,
  dateCriteria,
  carrier,
  stateEu,
  policyType,
  agent,
  paymentStatus,
  advisorPaid,
  loading: parentLoading,
  completedCategoryCodes,
  nonCanceledCategoryCodes,
  canceledCategoryCodes,
  onOpenPolicies,
  refreshTrigger,
}: IAdvisorsCompletedPoliciesTableProps) => {
  const [showModal, setShowModal] = useState(false)

  const buildColumnFilters = (entry: IAdvisorCompletedData, column: MetricColumn): Record<string, unknown> => {
    const base = {assignedTo: [entry.user.id]}
    switch (column) {
      case 'completed':
        return {...base, status: ['COMPLETED']}
      case 'carrierAccount':
        return {...base, status: ['COMPLETED'], hasCarrierAccount: true}
      case 'renewed':
        return {...base, status: ['RENEWED', 'RENOVACION_AUTOMATICA_MERCADO']}
      case 'totalActive':
        return {...base, status: completedCategoryCodes}
      case 'totalNonCanceled':
        return {...base, status: nonCanceledCategoryCodes}
      case 'canceled':
        return {...base, status: canceledCategoryCodes}
      case 'total':
        return base
    }
  }

  const openPoliciesFor = (entry: IAdvisorCompletedData, column: MetricColumn) =>
    onOpenPolicies?.(buildColumnFilters(entry, column))
  const [sortField, setSortField] = useState<SortField>('completed')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const [getTopAdvisors, {data, loading}] = useLazyQuery<IResAdvisorsCompleted>(
    GET_ADVISORS_COMPLETED_POLICIES,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [getAllAdvisors, {data: fullData, loading: loadingFull}] =
    useLazyQuery<IResAdvisorsCompleted>(GET_ADVISORS_COMPLETED_POLICIES, {
      fetchPolicy: 'no-cache',
    })

  useEffect(() => {
    getTopAdvisors({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
        limit: 10,
      },
    })
  }, [dateFrom, dateTo, dateCriteria, carrier, stateEu, policyType, agent, paymentStatus, advisorPaid, refreshTrigger, getTopAdvisors])

  const handleShowMore = () => {
    getAllAdvisors({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
        limit: 100,
      },
    })
    setShowModal(true)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder(field === 'advisor' ? 'asc' : 'desc')
    }
  }

  const advisorsData = data?.advisorsCompletedPolicies || []
  const fullAdvisorsData = fullData?.advisorsCompletedPolicies || []

  const sortedFullAdvisors = [...fullAdvisorsData].sort((a, b) => {
    if (sortField === 'advisor') {
      const nameA = `${a.user.firstName} ${a.user.lastName}`
      const nameB = `${b.user.firstName} ${b.user.lastName}`
      const comparison = nameA.localeCompare(nameB)
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'completed') {
      const comparison = a.completedCount - b.completedCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'completionPercentage') {
      const comparison = a.completionPercentage - b.completionPercentage
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'carrierAccount') {
      const comparison = a.carrierAccountCount - b.carrierAccountCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'renewed') {
      const comparison = a.renewedCount - b.renewedCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'renewalPercentage') {
      const comparison = a.renewalPercentage - b.renewalPercentage
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'totalActive') {
      const comparison = a.totalActiveCount - b.totalActiveCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'totalNonCanceled') {
      const comparison = a.totalNonCanceledCount - b.totalNonCanceledCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'canceled') {
      const comparison = a.canceledCount - b.canceledCount
      return sortOrder === 'asc' ? comparison : -comparison
    } else if (sortField === 'canceledPercentage') {
      const comparison = a.canceledPercentage - b.canceledPercentage
      return sortOrder === 'asc' ? comparison : -comparison
    } else {
      const comparison = a.totalCount - b.totalCount
      return sortOrder === 'asc' ? comparison : -comparison
    }
  })

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className='bi bi-arrow-down-up ms-1 text-muted'></i>
    return sortOrder === 'asc' ? (
      <i className='bi bi-arrow-up ms-1'></i>
    ) : (
      <i className='bi bi-arrow-down ms-1'></i>
    )
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  return (
    <>
      <Card className='card-flush h-100'>
        <Card.Header className='border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>
              Advisors performance metrics (Top 10)
            </span>
            <span className='text-muted fw-semibold fs-7'>
              Completed policies, renewal rates and total active policies per advisor
            </span>
          </h3>
          <div className='card-toolbar'>
            <button
              className='btn btn-sm btn-light-primary'
              onClick={handleShowMore}
              disabled={loading || parentLoading}
            >
              <i className='bi bi-plus-lg me-1'></i>
              See more
            </button>
          </div>
        </Card.Header>
        <Card.Body className='pt-0'>
          {(loading || parentLoading) && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loading && !parentLoading && advisorsData.length === 0 && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
              <p className='text-muted'>No data available for the selected period</p>
            </div>
          )}
          {!loading && !parentLoading && advisorsData.length > 0 && (
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th className='ps-0' style={{width: '3%'}}></th>
                    <th style={{width: '18%'}}>Advisor</th>
                    <th className='text-center' style={{width: '7%'}}>Completed</th>
                    <th className='text-center' style={{width: '7%'}}>% Comp.</th>
                    <th className='text-center' style={{width: '8%'}}>Carrier Acct.</th>
                    <th className='text-center' style={{width: '7%'}}>Renewed</th>
                    <th className='text-center' style={{width: '7%'}}>% Renew.</th>
                    <th className='text-center' style={{width: '8%'}}>Total Active</th>
                    <th className='text-center' style={{width: '8%'}}>Total no canceled</th>
                    <th className='text-center' style={{width: '7%'}}>Canceled</th>
                    <th className='text-center' style={{width: '7%'}}>% Cancel.</th>
                    <th className='text-center pe-0' style={{width: '7%'}}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {advisorsData.map((entry, index) => (
                    <tr key={entry.user.id}>
                      <td className='ps-0 text-center'>
                        <span className='text-muted fw-bold fs-7'>{index + 1}</span>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div
                            className='rounded-circle bg-light-success d-flex align-items-center justify-content-center me-3'
                            style={{width: 35, height: 35}}
                          >
                            <span className='text-success fw-bold fs-6'>
                              {getInitials(entry.user.firstName, entry.user.lastName)}
                            </span>
                          </div>
                          <div className='d-flex flex-column'>
                            <span className='text-gray-800 fw-bold fs-6'>
                              {entry.user.firstName} {entry.user.lastName}
                            </span>
                            {entry.assignmentGroupName && (
                              <span className='text-muted fs-7'>{entry.assignmentGroupName}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'completed')}
                        >
                          {formatNumber(entry.completedCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-success fs-7'>
                          {entry.completionPercentage.toFixed(2)}%
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'carrierAccount')}
                        >
                          {formatNumber(entry.carrierAccountCount)}/{formatNumber(entry.completedCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'renewed')}
                        >
                          {formatNumber(entry.renewedCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-primary fs-7'>
                          {entry.renewalPercentage.toFixed(2)}%
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'totalActive')}
                        >
                          {formatNumber(entry.totalActiveCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'totalNonCanceled')}
                        >
                          {formatNumber(entry.totalNonCanceledCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'canceled')}
                        >
                          {formatNumber(entry.canceledCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-danger fs-7'>
                          {entry.canceledPercentage.toFixed(2)}%
                        </span>
                      </td>
                      <td className='text-center pe-0'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'total')}
                        >
                          {formatNumber(entry.totalCount)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal for all advisors */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size='xl'
        dialogClassName='modal-90w'
      >
        <Modal.Header closeButton>
          <Modal.Title>Advisors completion rate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFull && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 200}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loadingFull && fullAdvisorsData.length === 0 && (
            <div className='text-center text-muted py-5'>No data available</div>
          )}
          {!loadingFull && fullAdvisorsData.length > 0 && (
            <div className='table-responsive' style={{maxHeight: '60vh', overflowY: 'auto'}}>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                  <tr className='fw-bold text-muted'>
                    <th className='ps-0 text-center' style={{width: '2%'}}></th>
                    <th
                      style={{width: '13%', cursor: 'pointer'}}
                      onClick={() => handleSort('advisor')}
                    >
                      Advisor {getSortIcon('advisor')}
                    </th>
                    <th className='text-center' style={{width: '12%'}}>Email</th>
                    <th
                      className='text-center'
                      style={{width: '6%', cursor: 'pointer'}}
                      onClick={() => handleSort('completed')}
                    >
                      Completed {getSortIcon('completed')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '6%', cursor: 'pointer'}}
                      onClick={() => handleSort('completionPercentage')}
                    >
                      % Comp. {getSortIcon('completionPercentage')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '7%', cursor: 'pointer'}}
                      onClick={() => handleSort('carrierAccount')}
                    >
                      Carrier Acct. {getSortIcon('carrierAccount')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '6%', cursor: 'pointer'}}
                      onClick={() => handleSort('renewed')}
                    >
                      Renewed {getSortIcon('renewed')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '6%', cursor: 'pointer'}}
                      onClick={() => handleSort('renewalPercentage')}
                    >
                      % Renew. {getSortIcon('renewalPercentage')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '7%', cursor: 'pointer'}}
                      onClick={() => handleSort('totalActive')}
                    >
                      Total Active {getSortIcon('totalActive')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '8%', cursor: 'pointer'}}
                      onClick={() => handleSort('totalNonCanceled')}
                    >
                      Total no canceled {getSortIcon('totalNonCanceled')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '6%', cursor: 'pointer'}}
                      onClick={() => handleSort('canceled')}
                    >
                      Canceled {getSortIcon('canceled')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '6%', cursor: 'pointer'}}
                      onClick={() => handleSort('canceledPercentage')}
                    >
                      % Cancel. {getSortIcon('canceledPercentage')}
                    </th>
                    <th
                      className='text-center pe-0'
                      style={{width: '6%', cursor: 'pointer'}}
                      onClick={() => handleSort('total')}
                    >
                      Total {getSortIcon('total')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFullAdvisors.map((entry, index) => (
                    <tr key={entry.user.id}>
                      <td className='ps-0 text-center'>
                        <span className='text-muted fw-bold fs-7'>{index + 1}</span>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div
                            className='rounded-circle bg-light-success d-flex align-items-center justify-content-center me-3'
                            style={{width: 35, height: 35}}
                          >
                            <span className='text-success fw-bold fs-6'>
                              {getInitials(entry.user.firstName, entry.user.lastName)}
                            </span>
                          </div>
                          <div className='d-flex flex-column'>
                            <span className='text-gray-800 fw-bold fs-6'>
                              {entry.user.firstName} {entry.user.lastName}
                            </span>
                            {entry.assignmentGroupName && (
                              <span className='text-muted fs-7'>{entry.assignmentGroupName}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className='text-center'>
                        <span className='text-muted fs-7'>{entry.user.email}</span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'completed')}
                        >
                          {formatNumber(entry.completedCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-success fs-7'>
                          {entry.completionPercentage.toFixed(2)}%
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'carrierAccount')}
                        >
                          {formatNumber(entry.carrierAccountCount)}/{formatNumber(entry.completedCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'renewed')}
                        >
                          {formatNumber(entry.renewedCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-primary fs-7'>
                          {entry.renewalPercentage.toFixed(2)}%
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'totalActive')}
                        >
                          {formatNumber(entry.totalActiveCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'totalNonCanceled')}
                        >
                          {formatNumber(entry.totalNonCanceledCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'canceled')}
                        >
                          {formatNumber(entry.canceledCount)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className='badge badge-light-danger fs-7'>
                          {entry.canceledPercentage.toFixed(2)}%
                        </span>
                      </td>
                      <td className='text-center pe-0'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesFor(entry, 'total')}
                        >
                          {formatNumber(entry.totalCount)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-secondary' onClick={() => setShowModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
